import styles from './index.module.css'

import { TbSparkles } from '@react-icons/all-files/tb/TbSparkles'
import { Button, Switch, Text } from '@radix-ui/themes'
import React from 'react'

type Props = {
  active?: boolean
  label?: string
  // Icon and switch only, for layouts the host knows are too tight for the label
  compact?: boolean
  onActivate: () => void
  onDeactivate: () => void
}

/**
 * Pill toggle that switches a search bar into AI (prompt) mode. Used by the
 * in-app search bar, Browse and the catalog header; callers own the mode state.
 */
const AiSearchToggle = ({
  active = false,
  label = 'AI Search',
  compact = false,
  onActivate,
  onDeactivate,
}: Props): React.ReactNode => {
  const handleToggle = () => {
    if (active) onDeactivate()
    else onActivate()
  }

  return (
    // Rendered as a span: the Radix Switch is itself a <button>, and buttons can't be nested
    <Button
      asChild
      variant="ghost"
      className={`${styles.btn} ${active ? styles.btnActive : ''} ${
        compact ? styles.compact : ''
      }`}
      aria-pressed={active}
      aria-label={label}
      onMouseDown={e => {
        e.preventDefault()
        handleToggle()
      }}
      // A span doesn't turn Enter/Space into a click like a native button does
      onKeyDown={e => {
        if (e.key !== 'Enter' && e.key !== ' ') return
        e.preventDefault()
        handleToggle()
      }}
      onClick={e => {
        if (e.detail === 0) handleToggle()
      }}
    >
      <span role="button" tabIndex={0}>
        <TbSparkles size={11} />
        <Text as="span" className={styles.label}>
          {label}
        </Text>
        <Switch
          size="1"
          checked={active}
          className={styles.switch}
          tabIndex={-1}
          aria-hidden
        />
      </span>
    </Button>
  )
}

export default AiSearchToggle
